import { useUserStore } from '@base/stores/user';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCredits, createCreditsCheckout } from '@base/api';
import toast from 'react-hot-toast';
import { formatCurrency } from '@utils/currency';
import { create } from 'zustand';
import { openPopUpTab } from '@utils/openPopUpTab';

interface CreditStore {
  isCheckoutSessionOpen: boolean;
  setIsCheckoutSessionOpen: (value: boolean) => void;
}

const useCreditStore = create<CreditStore>((set) => ({
  isCheckoutSessionOpen: false,
  setIsCheckoutSessionOpen: (value: boolean) =>
    set({ isCheckoutSessionOpen: value }),
}));

export const useCreditsCheckout = () => {
  const { authStoreInstance } = useUserStore();
  const accessToken = authStoreInstance?.accessToken;
  const projectId = authStoreInstance?.projectId;
  const { isCheckoutSessionOpen, setIsCheckoutSessionOpen } = useCreditStore();
  const { refetchCredits } = useCredits();

  const createCheckoutMutation = useMutation({
    mutationFn: () =>
      createCreditsCheckout({ accessToken, projectId }, { throwOnError: true }),
  });

  const handleAddCredits = async ({
    onClose = () => {},
  }: { onClose?: () => void } = {}) => {
    if (createCheckoutMutation.isPending || isCheckoutSessionOpen) return;

    const { url } = await createCheckoutMutation.mutateAsync();

    if (!url) {
      toast.error('Failed to create checkout session for credits');
      return;
    }

    setIsCheckoutSessionOpen(true);

    const popUp = openPopUpTab({
      url,
      onClose: () => {
        refetchCredits();
        setIsCheckoutSessionOpen(false);
        onClose();
      },
    });

    if (!popUp) {
      setIsCheckoutSessionOpen(false);
      return;
    }
  };

  return {
    createCheckout: createCheckoutMutation.mutate,
    createCheckoutAsync: createCheckoutMutation.mutateAsync,
    isCreatingCheckout:
      createCheckoutMutation.isPending || isCheckoutSessionOpen,
    handleAddCredits,
  };
};

export const useCredits = () => {
  const { authStoreInstance } = useUserStore();
  const accessToken = authStoreInstance?.accessToken;
  const projectId = authStoreInstance?.projectId;

  const {
    data: credits,
    isPending: isCreditsPending,
    isLoading: isCreditsLoading,
    isError: isCreditsError,
    refetch: refetchCredits,
  } = useQuery({
    queryKey: ['credits'],
    queryFn: () =>
      getCredits(
        {
          accessToken,
          projectId,
        },
        {
          throwOnError: true,
        },
      ),
    select: (data) => {
      const balance = data?.balance / 100;
      const formatted = formatCurrency(balance);

      return {
        balance,
        formatted,
      };
    },
  });

  return {
    credits,
    isCreditsLoading,
    isCreditsPending,
    isCreditsError,
    refetchCredits,
  };
};
